import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Stack, Typography } from '@mui/material'
import { getCodesDropdown } from 'api/accessCode'
import { getOrganizations } from 'api/organization'
import { inviteOrgTrainee } from 'api/users'
import { useAuthContext } from 'components/context/AuthProvider'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import FormSelect, { Option } from 'components/form-elements/FormSelect'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID, minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { EOp } from 'types'
import { object, string, TypeOf } from 'zod'

interface Props {
  isOpen: boolean
  handleClose: () => void
  onInvited?: () => void
  /** Admin portal mode: show an Organization selector and scope the Unit dropdown to it. */
  isAdmin?: boolean
}

const ORG_NAME_TOKEN = '{organizationName}'

const CONTACT_IS_REQUIRED = 'Provide at least an Email or a Mobile Phone'

const hasContact = (data: { email?: string; phoneNumber?: string }) =>
  Boolean(data.email?.trim() || data.phoneNumber?.trim())

const formSchema = object({
  firstName: string().min(1, FIELD_IS_REQUIRED),
  lastName: string().min(1, FIELD_IS_REQUIRED),
  email: string().email(EMAIL_IS_INVALID).optional().or(string().length(0)),
  organizationId: string().optional(),
  accessCodeId: string().min(1, FIELD_IS_REQUIRED),
  phoneNumber: string().min(10, PHONE_IS_INVALID).optional().or(string().length(0)),
  message: string().optional(),
})
  .refine(hasContact, { path: ['email'], message: CONTACT_IS_REQUIRED })
  .refine(hasContact, { path: ['phoneNumber'], message: CONTACT_IS_REQUIRED })

type FormType = TypeOf<typeof formSchema>

const ModalContent = ({ handleClose, onInvited, isAdmin }: Omit<Props, 'isOpen'>) => {
  const { user } = useAuthContext()

  // Builds the default email body. Inviter name comes from the logged-in user; `traineeName` and
  // `orgName` are filled from the form (tokens `{traineeName}`/`{organizationName}` until known).
  const buildMessage = useCallback(
    (traineeName: string, orgName: string) => {
      const inviterName =
        [user?.firstname, user?.lastname]
          .map((s) => s?.trim())
          .filter(Boolean)
          .join(' ') || '{inviterName}'
      return `Hi ${traineeName},\n${inviterName} invited you to register as a trainee for ${orgName} on MemoryCare Training Partner.\nPlease join our team`
    },
    [user],
  )

  // For an org user the organization is known at mount; for an admin it's chosen in the form.
  const initialOrgName = isAdmin ? ORG_NAME_TOKEN : user?.organizationName?.trim() || ORG_NAME_TOKEN
  const initialMessage = useMemo(
    () => buildMessage('{traineeName}', initialOrgName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const formProps = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: { message: initialMessage, organizationId: '', accessCodeId: '' },
  })
  const { handleSubmit, formState, watch, setValue, getValues } = formProps

  const firstName = watch('firstName')
  const lastName = watch('lastName')
  const organizationId = watch('organizationId')
  const orgIdNum = isAdmin ? Number(organizationId) || null : null

  // Admin only: organization selector. Org users have their org from the JWT and don't pick one.
  // Must be !!isAdmin: react-query treats `enabled: undefined` as enabled=true, which makes
  // org-portal users hit admin-only POST /organizations and get a Forbidden toast.
  const orgsQuery = useQuery('organizations', () => getOrganizations(), { enabled: !!isAdmin })

  const organizationOptions = useMemo<Option[]>(() => {
    if (!orgsQuery.data) {
      return []
    }
    return orgsQuery.data.data.rows.map((org) => ({ value: org.id, label: org.name }))
  }, [orgsQuery.data])

  // Current organization name for the email body: admin → selected org label; org user → their org.
  const organizationName = useMemo(() => {
    if (isAdmin) {
      const selected = organizationOptions.find((o) => String(o.value) === organizationId)
      return (selected?.label as string) || ORG_NAME_TOKEN
    }
    return user?.organizationName?.trim() || ORG_NAME_TOKEN
  }, [isAdmin, organizationOptions, organizationId, user])

  // Keep the invitee's name and organization in the message in sync with the form fields — but only
  // while the user hasn't manually edited the message (otherwise we'd clobber their changes).
  const autoMessageRef = useRef(initialMessage)

  useEffect(() => {
    const traineeName =
      [firstName, lastName]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(' ') || '{traineeName}'
    const candidate = buildMessage(traineeName, organizationName)
    if (getValues('message') === autoMessageRef.current) {
      setValue('message', candidate)
      autoMessageRef.current = candidate
    }
  }, [firstName, lastName, organizationName, buildMessage, getValues, setValue])

  // Reset the selected Unit whenever the admin changes organization.
  useEffect(() => {
    if (isAdmin) {
      setValue('accessCodeId', '')
    }
  }, [organizationId, isAdmin, setValue])

  const codesQuery = useQuery(
    ['codesDropdown', { onlyValid: true, orgId: orgIdNum }],
    () =>
      getCodesDropdown({
        onlyValid: true,
        filter:
          isAdmin && orgIdNum
            ? { organizationId: { type: 'number', op: EOp.equals, value: orgIdNum } }
            : undefined,
      }),
    { enabled: isAdmin ? !!orgIdNum : true },
  )

  const unitOptions = useMemo<Option[]>(() => {
    if (!codesQuery.data) {
      return []
    }
    return codesQuery.data.data.map((item) => ({
      value: item.id,
      label: item.specificUnitName,
    }))
  }, [codesQuery.data])

  const inviteMutation = useMutation(inviteOrgTrainee, {
    onSuccess: () => {
      handleClose()
      onInvited?.()
      NotificationSys.showSuccess('Invitation sent successfully')
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = ({
    firstName,
    lastName,
    email,
    accessCodeId,
    phoneNumber,
    message,
  }) => {
    const normalizedPhone = phoneNumber?.replace(/\D/g, '') || undefined

    inviteMutation.mutate({
      firstName,
      lastName,
      email: email?.trim() || undefined,
      accessCodeId: Number(accessCodeId),
      organizationId: isAdmin ? Number(organizationId) || undefined : undefined,
      phoneNumber: normalizedPhone,
      message: message || undefined,
    })
  }

  return (
    <FormProvider {...formProps}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
        <Stack spacing={2}>
          <FormInput label="First Name" name="firstName" required />
          <FormInput label="Last Name" name="lastName" required />
          <FormInput label="Email" name="email" />
          <FormPhone label="Mobile Phone" name="phoneNumber" />
          <Typography variant="caption" color="text.secondary">
            Provide at least an Email or a Mobile Phone.
          </Typography>
          {isAdmin && (
            <FormSelect
              label="Organization"
              name="organizationId"
              required
              options={organizationOptions}
              disabled={orgsQuery.isLoading}
            />
          )}
          <FormSelect
            label="Unit"
            name="accessCodeId"
            required
            options={unitOptions}
            disabled={codesQuery.isLoading || (isAdmin && !orgIdNum)}
          />
          <FormInput label="Text" name="message" multiline minRows={4} />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <LoadingButton variant="outlined" onClick={handleClose} sx={minButtonWidth}>
            Close
          </LoadingButton>
          <LoadingButton
            loading={inviteMutation.isLoading}
            disabled={!formState.isValid}
            variant="contained"
            type="submit"
            sx={minButtonWidth}
          >
            Send Invite
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}

export const InviteTraineeModal = ({ isOpen, handleClose, onInvited, isAdmin }: Props) => (
  <UIModal title="Invite Trainee" isOpen={isOpen} onClose={handleClose} width={480}>
    <ModalContent handleClose={handleClose} onInvited={onInvited} isAdmin={isAdmin} />
  </UIModal>
)
